import { useLocation, useNavigate } from '@tanstack/react-router'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui'
import { useUser } from '@/modules/auth'
import {
  useGettingStartedProject,
  useGettingStartedSteps,
  useGettingStartedWelcomed,
  useIsSingleProjectAccount,
  useOpenCreateProject,
  useShowGettingStartedWelcome,
} from '../hooks'
import { stepCountWord } from '../steps'
import { WelcomeChoiceCard } from './WelcomeChoiceCard'

const GETTING_STARTED_PATH = '/getting-started'

const greeting = (firstName: string | undefined) =>
  firstName ? `Welcome to CounselPro, ${firstName}` : 'Welcome to CounselPro'

// Asked once per user on first entry. Either choice, or closing it, counts.
export function GettingStartedWelcomeDialog() {
  const show = useShowGettingStartedWelcome()
  const { markWelcomed } = useGettingStartedWelcomed()
  const steps = useGettingStartedSteps()
  const openCreateProject = useOpenCreateProject()
  const singleProject = useIsSingleProjectAccount()
  const project = useGettingStartedProject()
  const firstName = useUser()?.user_metadata?.firstName
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const takeTheTour = () => {
    markWelcomed()
    navigate({ to: GETTING_STARTED_PATH })
  }

  const startAProject = () => {
    markWelcomed()
    openCreateProject()
  }

  // A single-project signup already has its project, so it goes straight there.
  const openYourProject = () => {
    markWelcomed()
    if (project) {
      navigate({ to: '/projects/$slug/files', params: { slug: project.slug } })
    } else {
      navigate({ to: '/projects' })
    }
  }

  return (
    <Dialog
      open={show && pathname !== GETTING_STARTED_PATH}
      onOpenChange={(open) => !open && markWelcomed()}
    >
      <DialogContent className="max-w-2xl gap-0 bg-[radial-gradient(ellipse_at_top_left,hsl(205_85%_55%/0.16),transparent_60%),radial-gradient(ellipse_at_bottom_right,hsl(var(--brand-deep)/0.14),transparent_60%)] bg-white p-6 sm:p-8">
        <DialogTitle className="text-2xl font-bold tracking-tight text-blue-7">
          {greeting(firstName)}
        </DialogTitle>
        <DialogDescription className="mt-2 text-base text-blue-6">
          Where would you like to start?
        </DialogDescription>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <WelcomeChoiceCard
            imageSrc="/getting-started/welcome-tour.webp"
            title="Take the tour"
            description={`${stepCountWord(steps.length)} short steps, from your first statement to a finished report. About four minutes.`}
            onChoose={takeTheTour}
          />
          {singleProject ? (
            <WelcomeChoiceCard
              imageSrc="/getting-started/welcome-start-project.webp"
              title="Open your project"
              description="Your project is ready. Drop in the bank and card statements."
              onChoose={openYourProject}
            />
          ) : (
            <WelcomeChoiceCard
              imageSrc="/getting-started/welcome-start-project.webp"
              title="Start a project"
              description="Name it, then drop in the bank and card statements."
              onChoose={startAProject}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
